import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  normalizeLookupId,
  normalizeLookupIds,
} from '../../../utils/responseHelpers';
import WizardLookupCreateDialog from './WizardLookupCreateDialog';

function mergeOptions(serverOptions, pendingOptions) {
  const byId = new Map();
  (serverOptions ?? []).forEach((opt) => {
    const id = normalizeLookupId(opt.id);
    if (id != null) byId.set(id, opt);
  });
  (pendingOptions ?? []).forEach((opt) => {
    const id = normalizeLookupId(opt.id);
    if (id != null) byId.set(id, opt);
  });
  return Array.from(byId.values());
}

function optionDisplayLabel(opt, labelKey) {
  if (!opt) return '';
  if (labelKey === 'role') {
    const role = String(opt.role || opt.jobTitle || '').trim();
    return role || (opt.id != null ? `#${opt.id}` : '');
  }
  const primary = String(opt[labelKey] ?? opt.name ?? opt.fullName ?? opt.vendorName ?? '').trim();
  return primary || (opt.id != null ? `#${opt.id}` : '');
}

const WizardLookupStep = ({
  step,
  selectedIds,
  onChange,
  lookups,
  onRefreshLookups,
  errors = {},
  assignmentTargetName = '',
  hideLabel = false,
}) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [pendingOptions, setPendingOptions] = useState([]);
  const [inputValue, setInputValue] = useState('');

  const serverOptions = lookups[step.lookupKey] ?? [];
  const options = useMemo(
    () => mergeOptions(serverOptions, pendingOptions),
    [serverOptions, pendingOptions]
  );
  const labelKey = step.optionLabel ?? 'name';
  const fieldName = step.stateKey;
  const selected = normalizeLookupIds(selectedIds);
  const targetLabel = assignmentTargetName?.trim() || 'this software';

  const selectedOptions = useMemo(
    () =>
      selected
        .map((id) => options.find((o) => normalizeLookupId(o.id) === id))
        .filter(Boolean),
    [selected, options]
  );

  useEffect(() => {
    setPendingOptions((prev) => {
      if (!prev.length || !serverOptions.length) return prev;
      const next = prev.filter(
        (p) => !serverOptions.some((o) => normalizeLookupId(o.id) === normalizeLookupId(p.id))
      );
      return next.length === prev.length ? prev : next;
    });
  }, [serverOptions]);

  const handleCreated = async (created, createValues) => {
    setPendingOptions((prev) => {
      const id = normalizeLookupId(created.id);
      if (prev.some((p) => normalizeLookupId(p.id) === id)) return prev;
      return [...prev, created];
    });

    onChange(normalizeLookupIds([...new Set([...selected, created.id])]));
    await onRefreshLookups?.();

    const createdName =
      created[labelKey] ||
      createValues?.[labelKey] ||
      createValues?.name ||
      createValues?.fullName ||
      createValues?.vendorName ||
      `#${created.id}`;

    setSuccessMessage(
      `${createdName} created and assigned to ${targetLabel}. It will be linked when you register the software.`
    );
  };

  const singularLabel = step.label.replace(/s$/, '').toLowerCase();
  const hasError = Boolean(errors[fieldName]);

  return (
    <Box>
      {!hideLabel ? (
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
          {step.label}
        </Typography>
      ) : null}

      <FormControl fullWidth error={hasError} size="small">
        <Autocomplete
          multiple
          size="small"
          options={options}
          value={selectedOptions}
          inputValue={inputValue}
          onInputChange={(_, value, reason) => {
            if (reason === 'input' || reason === 'clear') setInputValue(value);
            if (reason === 'reset') setInputValue('');
          }}
          onChange={(_, nextOptions) => {
            onChange(normalizeLookupIds(nextOptions.map((o) => o.id)));
            setInputValue('');
          }}
          getOptionLabel={(opt) => optionDisplayLabel(opt, labelKey)}
          isOptionEqualToValue={(a, b) =>
            normalizeLookupId(a?.id) === normalizeLookupId(b?.id)
          }
          filterOptions={(opts, state) => {
            const query = String(state.inputValue || '').trim().toLowerCase();
            if (!query) return opts;
            return opts.filter((opt) => {
              const name = optionDisplayLabel(opt, labelKey).toLowerCase();
              const extras = [opt.name, opt.fullName, opt.role, opt.jobTitle, opt.vendorName]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
              return name.includes(query) || extras.includes(query);
            });
          }}
          disableCloseOnSelect
          clearOnBlur={false}
          handleHomeEndKeys
          renderOption={(props, option, { selected: isSelected }) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                <Checkbox size="small" checked={isSelected} sx={{ mr: 1, p: 0.25 }} />
                {optionDisplayLabel(option, labelKey)}
              </li>
            );
          }}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  label={optionDisplayLabel(option, labelKey)}
                  {...tagProps}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              error={hasError}
              placeholder={selectedOptions.length ? 'Type to filter…' : 'Type to search…'}
            />
          )}
        />
        {hasError ? <FormHelperText>{errors[fieldName]}</FormHelperText> : null}
      </FormControl>

      {step.createConfig ? (
        <Box mt={1.5}>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreateOpen(true);
              setSuccessMessage('');
            }}
          >
            Add new {singularLabel}
          </Button>

          {successMessage ? (
            <Typography variant="caption" color="success.main" display="block" mt={1}>
              {successMessage}
            </Typography>
          ) : null}

          <WizardLookupCreateDialog
            open={createOpen}
            onClose={() => setCreateOpen(false)}
            createConfig={step.createConfig}
            saveLabel={step.createSaveLabel ?? 'Save & select'}
            onCreated={handleCreated}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default WizardLookupStep;
